import mixpanel from 'mixpanel-browser';

import { StoreModel } from '@/models/store';
import { MenuVersion } from '@/config/menuRollout';
import { MixpanelInitializer } from '@/modules/mixpanelInitializer';

export class EventHandler {
  public store: StoreModel = new StoreModel();

  constructor(data: any = {}) {
    this.store = data.store;
  }

  /**
   * `options` goes to mixpanel as is. When the page is about to leave, pass
   * `{ transport: 'sendBeacon', send_immediately: true }`: mixpanel batches by default and a hop
   * or redirect does not flush the queue, so `sendBeacon` alone still loses the event (AS-11622).
   */
  public track(event: string, extra: any = {}, options?: any) {
    try {
      // Check if Mixpanel has been initialized via MixpanelInitializer
      const initStatus = MixpanelInitializer.getInitializationStatus();
      if (!initStatus.isInitialized || !initStatus.distinctId) {
        // Silently skip tracking if not initialized - this is normal during app startup
        return;
      }

      mixpanel.track(event, {
        passedTrial: this.store.currentPackage.passedTrial,
        ...extra,
      }, options);
    } catch (err: any) {
      // no-op
    }
  }

  // Menu Version Tracking Events
  public trackMenuVersionLoaded(menuVersion: MenuVersion) {
    const isNewStore = this.store.id >= 13069;
    this.track('Menu Version Loaded', {
      menu_version: menuVersion,
      store_id: this.store.id,
      is_new_store: isNewStore,
      package_name: this.store.currentPackage.name,
      shop_name: this.store.shopName,
      is_trial: !this.store.currentPackage.passedTrial,
    });
  }

  public trackMenuItemClicked(menuVersion: MenuVersion, itemName: string, timeSinceLoad: number) {
    this.track('Menu Item Clicked', {
      menu_version: menuVersion,
      item_name: itemName,
      time_since_load: timeSinceLoad,
      store_id: this.store.id,
      package_name: this.store.currentPackage.name,
    });
  }

  // Locked Feature & Upgrade Modal Events
  public trackLockedFeatureClicked(featureName: string, requiredPackage: string) {
    this.track('Locked Feature Clicked', {
      feature_name: featureName,
      current_package: this.store.currentPackage.name,
      required_package: requiredPackage,
      store_id: this.store.id,
    });
  }

  public trackUpgradeModalShown(featureName: string, modalType: 'locked_feature' | 'menu_upgrade') {
    this.track('Upgrade Modal Shown', {
      trigger_feature: featureName,
      modal_type: modalType,
      store_id: this.store.id,
      current_package: this.store.currentPackage.name,
    });
  }

  public trackUpgradeModalLearnMoreClicked(featureName: string, timeOnModal: number) {
    this.track('Upgrade Modal Learn More Clicked', {
      feature_name: featureName,
      time_on_modal: timeOnModal,
      store_id: this.store.id,
      current_package: this.store.currentPackage.name,
    });
  }

  // Feature Usage & Funnel Analytics Events
  public trackFeatureStep(processName: string, stepName: string, extra: any = {}) {
    const eventName = `${this.formatProcessName(processName)} ${this.formatStepName(stepName)}`;

    this.track(eventName, {
      // Automatic store context (following existing pattern)
      store_id: this.store.id,
      shop_name: this.store.shopName,
      package_name: this.store.currentPackage.name,
      is_trial: !this.store.currentPackage.passedTrial,

      // Process context
      process_name: processName.toLowerCase().replace(/\s+/g, '_'),
      step_name: stepName.toLowerCase().replace(/\s+/g, '_'),

      // Custom properties
      ...extra,
    });
  }

  public trackFeatureCompleted(processName: string, extra: any = {}) {
    this.trackFeatureStep(processName, 'Completed', {
      completion_status: 'success',
      ...extra,
    });
  }

  public trackFeatureAbandoned(processName: string, reason?: string, extra: any = {}) {
    this.trackFeatureStep(processName, 'Abandoned', {
      completion_status: 'abandoned',
      abandon_reason: reason || 'unknown',
      ...extra,
    });
  }

  // Convenience methods for specific features
  public trackAutomatedPurchaseOrderStep(stepName: string, extra: any = {}) {
    this.trackFeatureStep('Automated Purchase Order', stepName, extra);
  }

  public trackReportGenerationStep(stepName: string, extra: any = {}) {
    this.trackFeatureStep('Report Generation', stepName, extra);
  }

  // Utility methods for formatting
  private formatProcessName(name: string): string {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private formatStepName(name: string): string {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
